import { memo } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import type { CommonPromoCodeProps } from '@wecasa/customer/src/components/basket/types';
import usePromoCodeCheck from '@wecasa/customer/src/components/promo-code/use-promo-code-check';
import { getBasketMode, resetPromotion } from '@wecasa/customer/src/ducks/basket';
import { getPromotionAlertContent } from '@wecasa/customer/src/utils/basket';

import { translate } from '@wecasa/i18n';

import useBoolean from '@wecasa/react-hooks/src/use-boolean';

import Alert from '@wecasa/web-ui/src/components/alert/alert';
import Modal from '@wecasa/web-ui/src/components/modal/modal';
import type { WithClassNameProps } from '@wecasa/web-ui/src/components/types';
import { DesktopAndTablet, Mobile, useIsMobile } from '@wecasa/web-ui/src/styles/media-queries';

import { trackAddPromoCode } from '../../utils/tracking';
import AddPromoCode from './add-promo-code';
import PromoCodeInput from './promo-code-input';

const t = translate('components.promo_code');

const Wrapper = styled.div`
  width: 100%;
  margin-top: 10px;
`;

const StyledAlert = styled(Alert)`
  margin: 5px 0 15px;
`;

export type PromoCodeProps = WithClassNameProps & CommonPromoCodeProps;

const PromoCode = ({
  cleaningPrestation,
  hasGift = false,
  promotion,
  totalPrice,
  universeReference,
  className,
  user,
}: PromoCodeProps) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const dispatch = useDispatch();
  const mode = useSelector(getBasketMode);
  const { value: inputVisible, setTrue: showInput, setFalse: hideInput } = useBoolean(false);
  const { checkPromoCode, error, isLoading } = usePromoCodeCheck({
    totalPrice,
    universeReference,
    promotion,
    cleaningPrestation,
    user,
  });
  const { type, content } = getPromotionAlertContent({ promotion, error });

  const onSubmit = ({ promoCode: code }: { promoCode: string }) => {
    if (isMobile) hideInput();
    checkPromoCode(code);
    trackAddPromoCode({ universe: universeReference, path: location.pathname, mode });
  };

  const onCloseAlert = () => {
    // When we close an error alert we also remove the promotion from the basket to avoid to have the user stucked with
    // an invalid promotion
    if (!promotion?.success) dispatch(resetPromotion());
  };

  // We never allow the user to use a promo code and a gift card at the same time.
  // And also we do not allow yet promo codes for the UK via the React app.
  if (hasGift) return null;

  return (
    <Wrapper className={className}>
      {/* isLoading is important here because onLoad it will remove the alert from the render tree and add a new */}
      {/* one after the api call to reset the isClosed flag on the alert if the user closed the previous one */}
      {content.length > 0 && !isLoading && (
        <StyledAlert type={type} content={content} isClosable onClose={onCloseAlert} />
      )}
      {/* Currently we allow only to add one promo code per potential */}
      {!promotion?.success && (
        <Form onSubmit={onSubmit} initialValues={{ promoCode: '' }}>
          {({ handleSubmit, invalid }) => (
            <>
              <Mobile>
                <AddPromoCode onClick={showInput} />
                <Modal
                  title={t('.modal_title')}
                  isOpen={inputVisible}
                  onClose={hideInput}
                  primaryButton={{
                    children: t('base.add'),
                    onPress: handleSubmit,
                    isDisabled: invalid,
                    isLoading,
                    type: 'submit',
                  }}
                >
                  <PromoCodeInput handleSubmit={handleSubmit} invalid={invalid} isLoading={isLoading} />
                </Modal>
              </Mobile>
              <DesktopAndTablet>
                {inputVisible ? (
                  <PromoCodeInput handleSubmit={handleSubmit} invalid={invalid} isLoading={isLoading} />
                ) : (
                  <AddPromoCode onClick={showInput} />
                )}
              </DesktopAndTablet>
            </>
          )}
        </Form>
      )}
    </Wrapper>
  );
};

export default memo(PromoCode);
